// src/constants/index.ts
var CURRENCIES = [
  { value: "USD", label: "US dollar", symbol: "$" },
  { value: "EUR", label: "Euro", symbol: "\u20AC" },
  { value: "JPY", label: "Japanese yen", symbol: "\xA5" },
  { value: "GBP", label: "Pound sterling", symbol: "\xA3" },
  { value: "AED", label: "United Arab Emirates dirham", symbol: "\u062F.\u0625" },
  { value: "AUD", label: "Australian dollar", symbol: "$" },
  { value: "ZAR", label: "South African Rand", symbol: "R" },
  { value: "AFN", label: "Afghan afghani", symbol: "\u060B" },
  { value: "ALL", label: "Albanian lek", symbol: "Lek" },
  { value: "AMD", label: "Armenian dram", symbol: "\u058F" },
  { value: "ANG", label: "Netherlands Antillean guilder", symbol: "\u0192" },
  { value: "AOA", label: "Angolan kwanza", symbol: "Kz" },
  { value: "ARS", label: "Argentine peso", symbol: "$" },
  { value: "AWG", label: "Aruban florin", symbol: "\u0192" },
  { value: "AZN", label: "Azerbaijani manat", symbol: "\u20BC" }
];
var DEFAULT_LOCALE = "en-IE";
var VatRate = /* @__PURE__ */ ((VatRate2) => {
  VatRate2[VatRate2["IE_STANDARD_VAT_RATE"] = 0.23] = "IE_STANDARD_VAT_RATE";
  VatRate2[VatRate2["GB_STANDARD_VAT_RATE"] = 0.2] = "GB_STANDARD_VAT_RATE";
  return VatRate2;
})(VatRate || {});
var IE_VAT_AMOUNT = 0.23 /* IE_STANDARD_VAT_RATE */;
var GB_VAT_AMOUNT = 0.2 /* GB_STANDARD_VAT_RATE */;
var LONG_DATE_FORMAT = "DD/MM/YYYY HH:mm a";
var SECOND_IN_MS = 1e3;
var SHORT_DATE_FORMAT = "DD/MM/YYYY";

// src/core/index.ts
function isValidJSON(text) {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}
function removeAllWhitespace(input) {
  return replaceAllWhitespace(input, "");
}
function replaceAllWhitespace(input, replaceWhitespaceWith) {
  return input.replace(/\s+/g, replaceWhitespaceWith);
}
function stringifyJsonData(data) {
  const stringifiedData = JSON.stringify(data).replace(/\\/g, "").replace(/"/g, "").replace(/:/g, ": ");
  return stringifiedData.replaceAll(",", "; ");
}
function truncateDecimalsAfter2Places(value) {
  return Math.floor(value * 100) / 100;
}
function capitalizeFirstLetters(value) {
  return value.toLowerCase().split(" ").map((word) => {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join(" ");
}
function convertStringsToUndefined(obj) {
  const result = {};
  for (const key in obj) {
    if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
      result[key] = convertStringsToUndefined(obj[key]);
    } else if (typeof obj[key] === "string") {
      result[key] = obj[key] === "" ? void 0 : obj[key];
    } else {
      result[key] = obj[key];
    }
  }
  return result;
}
function limitString(str, limit = 50) {
  return str.length > limit ? str.substring(0, limit) : str;
}
function parseEmailAddresses(emails) {
  if (!emails) {
    return [];
  }
  return emails.split(/[;,]/).map((email) => {
    return email.trim();
  }).filter((email) => {
    return email.length > 0;
  });
}
function renameKeys(jsonObj) {
  const renamedObj = {};
  for (const key in jsonObj) {
    if (Object.prototype.hasOwnProperty.call(jsonObj, key)) {
      const lastUnderscoreIndex = key.lastIndexOf("_");
      const newKey = key.substring(lastUnderscoreIndex + 1);
      renamedObj[newKey] = jsonObj[key];
    }
  }
  return renamedObj;
}
var toSentenceCase = (value) => {
  if (value === "NPI") {
    return value;
  }
  if (value === void 0 || value.length === 0) {
    return "";
  }
  const sanitizedString = value.replace(/[^a-zA-Z0-9\s]/g, " ");
  return `${sanitizedString.charAt(0).toUpperCase()}${sanitizedString.slice(1).toLocaleLowerCase()}`;
};

// src/finance/index.ts
function convertsCentsToCurrency(cents) {
  if (centsValueIsNotWholeNumber(cents)) {
    throw new TypeError("Invalid cents value");
  }
  let currencyValue = cents / 100;
  if (hasMoreThanTwoDecimalPlaces(currencyValue)) {
    const beforeDecimalPointPart = Math.trunc(currencyValue);
    const afterDecimalPointPart = Math.trunc(currencyValue % 1 * 100) / 100;
    currencyValue = beforeDecimalPointPart + afterDecimalPointPart;
    return currencyValue;
  }
  return currencyValue;
}
function convertsCurrencyToCents(currencyValue) {
  if (hasMoreThanTwoDecimalPlaces(currencyValue)) {
    throw new TypeError("Invalid currency value");
  }
  const beforeDecimalPointPart = Math.trunc(currencyValue);
  const afterDecimalPointPart = Math.trunc(Math.round(currencyValue % 1 * 100)) / 100;
  let centsValue = (beforeDecimalPointPart + afterDecimalPointPart) * 100;
  if (hasMoreThanTwoDecimalPlaces(centsValue)) {
    const beforeDecimalPointPart2 = Math.trunc(centsValue);
    const afterDecimalPointPart2 = Math.trunc(Math.round(centsValue % 1 * 100)) / 100;
    centsValue = beforeDecimalPointPart2 + afterDecimalPointPart2;
    return centsValue;
  }
  return centsValue;
}
function convertFixedAndPerItemValuesToCents(data) {
  traverseAndConvertToCents(data);
  return data;
}
function convertFixedAndPerItemValuesToCurrency(data) {
  traverseAndConvertToCurrency(data);
  return data;
}
function formatToFixedTwoDecimals(number) {
  return Number.parseFloat(number.toFixed(2));
}
function centsValueIsNotWholeNumber(cents) {
  return `${cents}`?.split(".").length > 1;
}
function hasMoreThanTwoDecimalPlaces(currency) {
  return `${currency}`?.split(".")[1]?.length > 2;
}
function traverseAndConvertToCents(obj) {
  for (const key in obj) {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      traverseAndConvertToCents(obj[key]);
    } else if (typeof obj[key] === "number" && obj.type !== "PERCENTAGE" && !key.toLowerCase().includes("percentage") && !key.toLowerCase().includes("id")) {
      obj[key] = convertsCurrencyToCents(obj[key]);
    }
  }
}
function traverseAndConvertToCurrency(obj) {
  for (const key in obj) {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      traverseAndConvertToCurrency(obj[key]);
    } else if (typeof obj[key] === "number" && obj.type !== "PERCENTAGE" && !key.toLowerCase().includes("percentage")) {
      obj[key] = convertsCentsToCurrency(obj[key]);
    }
  }
}

// src/lodash/index.ts
function capitalize(value) {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}
function camelCase(value) {
  return value.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase().replace(/[-_\s]+(.)?/g, (_, c) => {
    return c ? c.toUpperCase() : "";
  }).replace(/^[A-Z]/, (match) => {
    return match.toLowerCase();
  });
}
function debounce(func, wait) {
  let timeout;
  const debounced = (...args) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };
  return debounced;
}
function filter(array, predicate) {
  return array.filter(predicate);
}
function findKey(obj, predicate) {
  for (const key in obj) {
    if (predicate(obj[key], key)) {
      return key;
    }
  }
  return void 0;
}
function flatMap(array, iteratee) {
  return array.reduce((acc, value) => {
    return acc.concat(iteratee(value));
  }, []);
}
function groupBy(array, key) {
  return array.reduce(
    (result, item) => {
      const groupKey = String(item[key]);
      if (!result[groupKey]) {
        result[groupKey] = [];
      }
      result[groupKey].push(item);
      return result;
    },
    {}
  );
}
function includes(collection, value) {
  if (typeof collection === "string") {
    return collection.includes(value);
  }
  for (const item of collection) {
    if (isEqual(item, value)) {
      return true;
    }
  }
  return false;
}
function indexOf(array, value) {
  for (let idx = 0; idx < array.length; idx++) {
    if (isEqual(array[idx], value)) {
      return idx;
    }
  }
  return -1;
}
function isEmpty(value) {
  if (value === null) return true;
  if (typeof value === "string" || Array.isArray(value)) return value.length === 0;
  if (value instanceof Map || value instanceof Set) return value.size === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}
function isEqual(value, other) {
  if (value === other) {
    return true;
  }
  if (typeof value !== "object" || value === null || typeof other !== "object" || other === null) {
    return false;
  }
  const keysA = Object.keys(value);
  const keysB = Object.keys(other);
  if (keysA.length !== keysB.length) {
    return false;
  }
  for (const key of keysA) {
    if (!keysB.includes(key) || !isEqual(
      value[key],
      other[key]
    )) {
      return false;
    }
  }
  return true;
}
function isNil(value) {
  return value === null || value === void 0;
}
function isNumber(value) {
  return typeof value === "number" && !Number.isNaN(value);
}
function isObject(value) {
  return value !== null && (typeof value === "object" || typeof value === "function");
}
function mapKeys(obj, iteratee) {
  const result = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const newKey = iteratee(obj[key], key);
      result[newKey] = obj[key];
    }
  }
  return result;
}
function merge(target, ...sources) {
  if (!target || typeof target !== "object") {
    throw new Error("Target must be a non-null object.");
  }
  if (!sources.length) return target;
  const source = sources.shift();
  if (source && typeof source === "object") {
    mergeObject(target, source);
  }
  return merge(target, ...sources);
}
function mergeObject(target, source) {
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      mergeKey(target, source, key);
    }
  }
}
function mergeKey(target, source, key) {
  if (typeof source[key] === "object" && source[key] !== null) {
    if (!target[key] || typeof target[key] !== "object") {
      target[key] = {};
    }
    merge(target[key], source[key]);
  } else {
    target[key] = source[key];
  }
}
function omit(obj, keys) {
  const result = {};
  for (const key in obj) {
    if (!keys.includes(key)) {
      result[key] = obj[key];
    }
  }
  return result;
}
function orderBy(array, iteratees, orders = []) {
  return array.slice().sort((a, b) => {
    for (let i = 0; i < iteratees.length; i++) {
      const key = iteratees[i];
      const order = orders[i] || "asc";
      if (a[key] < b[key]) return order === "asc" ? -1 : 1;
      if (a[key] > b[key]) return order === "asc" ? 1 : -1;
    }
    return 0;
  });
}
function pick(obj, keys) {
  const result = {};
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key];
    }
  }
  return result;
}
function replace(value, pattern, replacement) {
  return value.replace(pattern, replacement);
}
function set(obj, path, value) {
  if (typeof path === "string") {
    path = path.split(".").filter((key) => {
      return key.length;
    });
  }
  path.reduce(
    (acc, key, idx) => {
      if (idx === path.length - 1) {
        acc[key] = value;
      } else if (!acc[key] || typeof acc[key] !== "object") {
        acc[key] = {};
      }
      return acc[key];
    },
    obj
  );
  return obj;
}
function sortBy(array, iteratees) {
  return array.slice().sort((a, b) => {
    for (const key of iteratees) {
      if (a[key] < b[key]) return -1;
      if (a[key] > b[key]) return 1;
    }
    return 0;
  });
}
function sum(array) {
  return array.reduce((acc, curr) => {
    return acc + curr;
  }, 0);
}
function toNumber(value) {
  const num = Number(value);
  return Number.isNaN(num) ? 0 : num;
}
function union(...arrays) {
  const set2 = /* @__PURE__ */ new Set();
  arrays.forEach((array) => {
    return array.forEach((item) => {
      return set2.add(item);
    });
  });
  return Array.from(set2);
}
function uniq(array) {
  return Array.from(new Set(array));
}
function uniqBy(array, iteratee) {
  const seen = /* @__PURE__ */ new Set();
  return array.filter((item) => {
    const computed = iteratee(item);
    if (seen.has(computed)) {
      return false;
    }
    seen.add(computed);
    return true;
  });
}
function uniqWith(array, comparator) {
  return array.filter((item, index) => {
    return array.findIndex((otherItem) => {
      return comparator(item, otherItem);
    }) === index;
  });
}
export {
  CURRENCIES,
  DEFAULT_LOCALE,
  GB_VAT_AMOUNT,
  IE_VAT_AMOUNT,
  LONG_DATE_FORMAT,
  SECOND_IN_MS,
  SHORT_DATE_FORMAT,
  VatRate,
  camelCase,
  capitalize,
  capitalizeFirstLetters,
  convertFixedAndPerItemValuesToCents,
  convertFixedAndPerItemValuesToCurrency,
  convertStringsToUndefined,
  convertsCentsToCurrency,
  convertsCurrencyToCents,
  debounce,
  filter,
  findKey,
  flatMap,
  formatToFixedTwoDecimals,
  groupBy,
  includes,
  indexOf,
  isEmpty,
  isEqual,
  isNil,
  isNumber,
  isObject,
  isValidJSON,
  limitString,
  mapKeys,
  merge,
  omit,
  orderBy,
  parseEmailAddresses,
  pick,
  removeAllWhitespace,
  renameKeys,
  replace,
  replaceAllWhitespace,
  set,
  sortBy,
  stringifyJsonData,
  sum,
  toNumber,
  toSentenceCase,
  truncateDecimalsAfter2Places,
  union,
  uniq,
  uniqBy,
  uniqWith
};
//# sourceMappingURL=index.js.map